import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Heading } from '@chakra-ui/react';

import { Markdown } from '~/components/markdown';
import { ShapesBanner } from '~/components/shapes-banner';
import { TemplateButton } from '~/components/template-button';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { EnterpriseResourceContainer } from '~/features/enterprise-resource/components/enterprise-resource-container';
import { EnterpriseResourceHeroWithLargeImage } from '~/features/enterprise-resource/components/enterprise-resource-hero-with-large-image';
import { getEnterpriseInfographicContent } from '~/features/enterprise-resource/controllers/get-enterprise-infographic-content';
import { EnterpriseInfographicContent } from '~/features/enterprise-resource/models/enterprise-infographic-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

type EnterpriseInfographicPageProps = {
  page: Page;
  enterpriseInfographicContent: EnterpriseInfographicContent;
};

export default function EnterpriseInfographicPage({
  page,
  enterpriseInfographicContent,
}: EnterpriseInfographicPageProps) {
  const infographicData = enterpriseInfographicContent.infographicData;
  const fileUrl = infographicData.file.asset.url;

  function handlePrimaryCtaClick() {
    if (infographicData.primaryCta?.buttonAction === 'Coded Action') {
      window.open(fileUrl, '_blank');
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Infographic CTA Clicked',
      label: 'infographic_cta_clicked',
      value: enterpriseInfographicContent.slug,
    });
  }

  function handlePageCtaClick() {
    if (infographicData.pageCta?.buttonAction === 'Coded Action') {
      window.open(fileUrl, '_blank');
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Infographic CTA Clicked',
      label: 'infographic_cta_clicked',
      value: enterpriseInfographicContent.slug,
    });
  }

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseInfographic} />
      <PageLayout overflowX='hidden' withoutVerticalPadding>
        <EnterpriseResourceHeroWithLargeImage
          description={infographicData.description}
          fileUrl={fileUrl}
          image={infographicData.heroImage}
          primaryCta={infographicData.primaryCta}
          relatedCompanies={infographicData.relatedCompanies}
          resourceTypeTitle={infographicData.resourceTypeTitle}
          title={infographicData.title}
        />

        <EnterpriseResourceContainer
          marginTop={{ base: '40px', md: '64px', lg: '80px' }}
          maxWidth={{ base: '736px', xl: '736px' }}
        >
          <Heading as='h2' size='marketing-2xl'>
            {infographicData.pageContent.title}
          </Heading>

          <Box marginTop='40px'>
            <Markdown markdownText={infographicData.pageContent.body} />
          </Box>

          {infographicData.pageCta && (
            <Box marginTop='40px'>
              <TemplateButton {...infographicData.pageCta} openInNewTab={true} onClick={handlePageCtaClick} />
            </Box>
          )}
        </EnterpriseResourceContainer>

        <Box h={{ base: '80px', md: '120px' }} />

        {infographicData.shapesBanner && (
          <ShapesBanner
            {...infographicData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={handlePrimaryCtaClick}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseInfographicPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise Infographic slug not found', {
      file: 'src/pages/Infographic/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise Infographic slug not found');
  }

  const [page, enterpriseInfographicContent] = await Promise.all([
    getPage('/infographic'),
    getEnterpriseInfographicContent(slug),
  ]);

  if (!enterpriseInfographicContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseInfographicContent has seo, override default page seo
  if (enterpriseInfographicContent.seo) {
    page.seo = enterpriseInfographicContent.seo;
  }

  return {
    props: {
      page,
      enterpriseInfographicContent,
    },
  };
}
